import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { Sparkles, ChevronRight, ArrowRight, Scissors, List } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import SearchAutocomplete from '../../components/SearchAutocomplete';
import { getCategoryIconComponent } from '../../components/LaundryIcons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MenuPage() {
  const { t, lang, isRTL } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [priceLists, setPriceLists] = useState([]);
  const [defaultPriceList, setDefaultPriceList] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch default price list first
        const defaultRes = await axios.get(`${API}/price-lists/default`);
        setDefaultPriceList(defaultRes.data);
        
        // Fetch categories for the default price list
        const catsRes = await axios.get(`${API}/categories?price_list_id=${defaultRes.data.id}&active_only=true`);
        setCategories(catsRes.data);
        
        // Fetch all active price lists (for multi-list navigation)
        const allPLRes = await axios.get(`${API}/price-lists?active_only=true`);
        setPriceLists(allPLRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSearch = (query) => {
    if (query.trim().length < 2) {
      setSearchResults(null);
      return;
    }
    const priceListFilter = defaultPriceList ? `&price_list_id=${defaultPriceList.id}` : '';
    axios.get(`${API}/products?search=${encodeURIComponent(query)}&active_only=true${priceListFilter}`)
      .then(res => setSearchResults(res.data))
      .catch(console.error);
  };

  const getName = (item) => lang === 'ar' ? item.name_ar : item.name_en;

  const formatPrice = (price) => {
    if (!price || price <= 0) return null;
    const formatted = Number(price).toFixed(2);
    return `${formatted} ${t('sar')}`;
  };

  const getServiceLabel = (type) => {
    const labels = { 
      regular_wash: t('regularWash'), 
      dry_clean: t('dryClean'), 
      pressing: t('pressing') 
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#0056A6] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="fade-in" data-testid="menu-page">
      {/* Hero Section */}
      <section className="relative bg-[#0056A6] overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-white rounded-full translate-y-1/2 -translate-x-1/2" />
        </div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-16 md:py-24 relative">
          <div className="max-w-2xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/15 rounded-full px-4 py-1.5 mb-6">
              <Sparkles className="h-4 w-4 text-white" />
              <span className="text-sm font-medium text-white/90">{t('heroSubtitle')}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-4">
              {t('heroTitle')}
            </h1>
            <p className="text-lg text-white/70 mb-8 leading-relaxed">
              {t('heroDescription')}
            </p>
            <a href="#categories" className="inline-block">
              <Button className="bg-white text-[#0056A6] hover:bg-white/90 h-12 px-8 text-base font-semibold rounded-xl shadow-lg shadow-black/10" data-testid="browse-menu-btn">
                {t('browseMenu')}
                <ArrowRight className={`h-4 w-4 ms-2 ${isRTL ? 'rotate-180' : ''}`} />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Price Lists Switcher (if more than one active price list) */}
      {priceLists.length > 1 && (
        <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6 border-b border-slate-100">
          <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-hide">
            <span className="text-sm text-slate-500 whitespace-nowrap flex items-center gap-2">
              <List className="h-4 w-4" />
              {t('priceLists')}:
            </span>
            {priceLists.map(pl => (
              <Link
                key={pl.id}
                to={`/price-list/${pl.id}`}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  pl.is_default 
                    ? 'bg-[#0056A6] text-white shadow-md' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid={`price-list-link-${pl.id}`}
              >
                {getName(pl)}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Search Bar with Autocomplete */}
      <div className="container mx-auto px-4 md:px-6 lg:px-8 -mt-6 relative z-10">
        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg shadow-slate-200/50 border border-slate-100">
            <SearchAutocomplete onSearch={handleSearch} />
          </div>
        </div>
      </div>

      {/* Search Results */}
      {searchResults && (
        <div className="container mx-auto px-4 md:px-6 lg:px-8 mt-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800">
                {searchResults.length} {t('products')}
              </h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setSearchResults(null)}
                className="text-slate-500"
              >
                {t('close')}
              </Button>
            </div>
            {searchResults.length === 0 ? (
              <p className="text-slate-500 text-center py-8">{t('noProducts')}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {searchResults.map(product => (
                  <div key={product.id} className="product-card bg-white rounded-xl border border-slate-100 p-4" data-testid={`search-result-${product.id}`}>
                    <div className="flex gap-4">
                      <div className="w-14 h-14 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <Scissors className="h-6 w-6 text-[#0056A6]/50" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-slate-800 truncate">{getName(product)}</h4>
                        <div className="flex gap-1.5 mt-1.5 flex-wrap">
                          {product.service_types?.map(type => (
                            <Badge key={type} variant="secondary" className="text-[0.65rem] bg-blue-50 text-[#0056A6] border-0">
                              {getServiceLabel(type)}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                    {/* Prices */}
                    <div className="mt-3 pt-3 border-t border-slate-50 grid grid-cols-3 gap-2 text-center">
                      {product.laundry_price > 0 && (
                        <div>
                          <p className="text-[0.65rem] text-slate-400">{t('regularWash')}</p>
                          <p className="text-sm font-bold text-[#0056A6]">{formatPrice(product.laundry_price)}</p>
                        </div>
                      )}
                      {product.dry_clean_price > 0 && (
                        <div>
                          <p className="text-[0.65rem] text-slate-400">{t('dryClean')}</p>
                          <p className="text-sm font-bold text-emerald-600">{formatPrice(product.dry_clean_price)}</p>
                        </div>
                      )}
                      {product.pressing_price > 0 && (
                        <div>
                          <p className="text-[0.65rem] text-slate-400">{t('pressing')}</p>
                          <p className="text-sm font-bold text-slate-600">{formatPrice(product.pressing_price)}</p>
                        </div>
                      )}
                    </div>
                    <p className="text-[0.6rem] text-slate-300 text-center mt-1">{t('priceIncVat')}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Categories Grid */}
      {!searchResults && (
        <section id="categories" className="container mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16" data-testid="categories-section">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
              {t('ourServices')}
            </h2>
            <p className="text-base text-slate-500 mt-2">{t('heroSubtitle')}</p>
          </div>

          {categories.length === 0 ? (
            <p className="text-center text-slate-400 py-12">{t('noCategories')}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {categories.map((cat, idx) => {
                const IconComponent = getCategoryIconComponent(cat.icon);
                return (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.id}`}
                    className={`category-card group slide-up stagger-${idx + 1}`}
                    data-testid={`category-card-${cat.id}`}
                  >
                    <div className="rounded-2xl border border-slate-100 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] overflow-hidden hover:shadow-lg hover:border-[#0056A6]/20 transition-all duration-300">
                      <div className="p-6">
                        {cat.image ? (
                          <div className="w-16 h-16 rounded-2xl overflow-hidden mb-4 group-hover:scale-110 transition-transform duration-300">
                            <img src={cat.image} alt={getName(cat)} className="w-full h-full object-cover" />
                          </div>
                        ) : (
                          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0056A6]/10 to-[#0056A6]/5 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                            <IconComponent className="h-8 w-8" color="#0056A6" />
                          </div>
                        )}
                        <h3 className="font-bold text-slate-800 text-lg mb-1 group-hover:text-[#0056A6] transition-colors">
                          {getName(cat)}
                        </h3>
                        <p className="text-sm text-slate-400">
                          {cat.subcategory_count || 0} {t('subcategories')} • {cat.product_count || 0} {t('items')}
                        </p>
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-50">
                          <span className="text-sm font-medium text-[#0056A6]">{t('viewAll')}</span>
                          <ChevronRight className={`h-5 w-5 text-[#0056A6] group-hover:translate-x-1 transition-transform ${isRTL ? 'rotate-180 group-hover:-translate-x-1' : ''}`} />
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
